<template>
  <div class="col-12">
    <div class="mb-4 ps-3 no-print">
      <button class="btn btn-primary btn-sm" @click="printSchedule">
        <i class="fas fa-print ms-2"></i>
        {{ t('schedule.print') }}
      </button>
    </div>

    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
            {{ t('schedule.my_schedule_title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <LoadingSpinner v-if="loading" :message="t('schedule.load_failed')" class="py-3 py-5" />

        <div v-else-if="classesGrouped.length === 0" class="text-center text-muted py-5">
          <i class="fas fa-calendar-times fa-2x mb-2"></i>
          <p>{{ t('schedule.no_classes') }}</p>
        </div>

        <!-- One table: Class -> Group -> Schedule, so a teacher who teaches
             more than one class can see each class's groups and, for every
             group, its own weekly day/period schedule in the same place. -->
        <div v-else class="table-responsive p-4" id="schedule-to-print">
          <table class="table table-bordered align-middle teacher-schedule-table">
            <thead>
              <tr>
                <th class="bg-light">{{ t('schedule.class') }}</th>
                <th class="bg-light">{{ t('schedule.group') }}</th>
                <th class="bg-light">{{ t('schedule.weekly_schedule') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="cls in classesGrouped" :key="cls.classId">
                <tr v-for="(grp, idx) in cls.groups" :key="grp.groupId">
                  <td
                    v-if="idx === 0"
                    :rowspan="cls.groups.length"
                    class="fw-bold bg-light text-center class-cell"
                  >
                    <i class="fas fa-chalkboard ms-2"></i>
                    {{ cls.className }}
                  </td>

                  <td class="text-center group-cell">
                    <div class="fw-bold">{{ grp.groupName }}</div>
                    <small class="text-muted">{{ grp.subjects.join('، ') }}</small>
                  </td>

                  <td class="p-2">
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered text-center mb-0 group-schedule-table">
                        <thead>
                          <tr>
                            <th class="bg-light">{{ t('schedule.day_period') }}</th>
                            <th
                              v-for="period in periodsWithTimes"
                              :key="period.value"
                              class="bg-light"
                            >
                              {{ period.label }}<br />
                              <small class="text-muted">{{ period.time }}</small>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="day in days" :key="day">
                            <td class="bg-light fw-bold">{{ t(`${day}`) }}</td>

                            <template v-if="day === 'السبت' && !saturdayHasSchedulesIn(schedulesFor(cls.classId, grp.groupId))">
                              <td
                                :colspan="periodsWithTimes.length"
                                class="empty-day"
                              >
                                {{ t('schedule.no_classes') }}
                              </td>
                            </template>

                            <template v-else>
                              <td
                                v-for="period in getPeriodsForDayIn(schedulesFor(cls.classId, grp.groupId), day)"
                                :key="period.value"
                                :class="{
                                  'text-primary fw-bold': hasScheduleIn(schedulesFor(cls.classId, grp.groupId), day, period.backendValue),
                                  'bg-secondary text-white': period.value === 'استراحة'
                                }"
                              >
                                <template v-if="period.value === 'استراحة'">
                                  {{ t('schedule.break') }}
                                </template>
                                <template v-else>
                                  {{ getCellIn(schedulesFor(cls.classId, grp.groupId), day, period.backendValue)?.subject?.name || '---' }}
                                </template>
                              </td>
                            </template>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();
const toast = useToast();

const schedules = ref([]);
const assignments = ref([]);
const loading = ref(false);
const scheduleConfig = ref({
  start_time: "08:00",
  period_duration: 45,
  total_periods: 6,
  break_duration: 30,
  break_after_period: 2
});

const days = [
  "السبت",
  "الأحد",
  "الاثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
];

const periodsWithTimes = computed(() => {
  const periods = [];
  const startTime = new Date(`2000-01-01T${scheduleConfig.value.start_time}`);
  const periodDuration = scheduleConfig.value.period_duration;
  const breakDuration = scheduleConfig.value.break_duration;
  const totalPeriods = scheduleConfig.value.total_periods;
  const breakAfterPeriod = scheduleConfig.value.break_after_period;

  let currentTime = new Date(startTime.getTime());

  for (let i = 1; i <= totalPeriods; i++) {
    const periodEnd = new Date(currentTime.getTime() + periodDuration * 60000);

    const periodName = getPeriodName(i);
    const backendPeriodName = getBackendPeriodName(i);

    periods.push({
      value: periodName,
      backendValue: backendPeriodName,
      label: `${t('schedule.period')} ${periodName}`,
      time: `${formatTime(currentTime)} - ${formatTime(periodEnd)}`
    });

    if (i === breakAfterPeriod) {
      const breakStart = new Date(periodEnd.getTime());
      const breakEnd = new Date(breakStart.getTime() + breakDuration * 60000);

      periods.push({
        value: "استراحة",
        backendValue: "استراحة",
        label: t('schedule.break'),
        time: `${formatTime(breakStart)} - ${formatTime(breakEnd)}`
      });

      currentTime = new Date(breakEnd.getTime());
    } else {
      currentTime = new Date(periodEnd.getTime());
    }
  }

  return periods;
});

const getPeriodName = (number) => {
  const periodNames = {
    1: t('schedule.first'),
    2: t('schedule.second'),
    3: t('schedule.third'),
    4: t('schedule.fourth'),
    5: t('schedule.fifth'),
    6: t('schedule.sixth'),
    7: t('schedule.seventh')
  };
  return periodNames[number] || number.toString();
};

const getBackendPeriodName = (number) => {
  const backendPeriodNames = {
    1: "الاولى",
    2: "الثانية",
    3: "الثالثة",
    4: "الرابعة",
    5: "الخامسة",
    6: "السادسة",
    7: "السابعة"
  };
  return backendPeriodNames[number] || number.toString();
};

const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" });
};

// Classes -> Groups the teacher is assigned to (from ClassGroupTeacher via
// /teacher/schedule's `assignments`), independent of whether a group's
// schedule has been filled in yet. One class can have several groups, and
// one group can list several subjects if the teacher teaches more than one
// subject there.
const classesGrouped = computed(() => {
  const classMap = new Map();

  for (const a of assignments.value) {
    const classId = a.class?.id;
    const groupId = a.group?.id;
    if (!classId || !groupId) continue;

    if (!classMap.has(classId)) {
      classMap.set(classId, { classId, className: a.class?.name || '', groups: new Map() });
    }
    const cls = classMap.get(classId);

    if (!cls.groups.has(groupId)) {
      cls.groups.set(groupId, { groupId, groupName: a.group?.name || '', subjects: [] });
    }
    const grp = cls.groups.get(groupId);
    const subjectName = a.subject?.name;
    if (subjectName && !grp.subjects.includes(subjectName)) {
      grp.subjects.push(subjectName);
    }
  }

  return Array.from(classMap.values()).map((cls) => ({
    classId: cls.classId,
    className: cls.className,
    groups: Array.from(cls.groups.values()),
  }));
});

// Schedule rows bucketed by "classId-groupId" so each group's grid only
// shows its own periods, even if two different classes happen to share a
// day/period slot.
const schedulesByGroup = computed(() => {
  const map = new Map();
  for (const s of schedules.value) {
    const key = `${s.class?.id}-${s.group?.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  return map;
});

const schedulesFor = (classId, groupId) => schedulesByGroup.value.get(`${classId}-${groupId}`) || [];

const saturdayHasSchedulesIn = (list) => list.some((s) => s.day === "السبت");

const getPeriodsForDayIn = (list, day) => {
  if (day === "السبت" && !saturdayHasSchedulesIn(list)) {
    return [];
  }
  return periodsWithTimes.value;
};

const hasScheduleIn = (list, day, period) => list.some((s) => s.day === day && s.period === period);

const getCellIn = (list, day, period) => list.find((s) => s.day === day && s.period === period) || null;

const fetchTeacherSchedule = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/teacher/schedule`);
    schedules.value = Array.isArray(response.data.data) ? response.data.data : [];
    assignments.value = Array.isArray(response.data.assignments) ? response.data.assignments : [];
  } catch (error) {
    toast.error(error.response?.data?.message || t('schedule.load_failed'));
    schedules.value = [];
    assignments.value = [];
  } finally {
    loading.value = false;
  }
};

const printSchedule = () => {
  const wrapper = document.getElementById('schedule-to-print');
  const printContent = wrapper ? wrapper.innerHTML : '';
  const printWindow = window.open('', '_blank');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <title>${t('schedule.my_schedule_title')}</title>
      <meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 20px; }

        table { width: 100%; border-collapse: collapse; margin: 0 auto 16px; }
        th, td { padding: 6px; text-align: center; border: 1px solid #000; word-break: break-word; }
        thead th { font-weight: 700; }

        .bg-light { background-color: #f8f9fa !important; }
        .bg-secondary { background-color: #6c757d !important; color: white !important; }
        .text-muted { color: #6c757d !important; }
        .text-primary { color: #0d6efd !important; }
        .fw-bold { font-weight: 700 !important; }

        .empty-day { background-color: #f8f9fa !important; color: #6c757d !important; font-style: italic; }

        @page { size: A4 landscape; margin: 12mm; }
        .print-footer { text-align: left; margin-top: 16px; font-size: 12px; }

        @media print {
          body { padding: 0; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <h2>${t('schedule.my_schedule_title')}</h2>
      ${printContent}
      <div class="print-footer">
        ${t('schedule.printed_at')} ${new Date().toLocaleString()}
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

onMounted(() => {
  fetchTeacherSchedule();
});
</script>

<style scoped>
.group-schedule-table td,
.group-schedule-table th {
  vertical-align: middle;
  min-width: 90px;
}

.class-cell,
.group-cell {
  min-width: 110px;
}

.empty-day {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  font-style: italic;
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
